import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import { getGaps, updateGap } from "../services/api";

export default function AdminView() {
  const [gaps, setGaps] = useState([]);
  const [filter, setFilter] = useState("all");

  const fetchGaps = useCallback(async () => {
    const params = {};
    if (filter === "unanswered") params.wasAnswered = false;
    if (filter === "flagged") params.flagged = true;
    if (filter === "unreviewed") params.reviewed = false;
    const data = await getGaps(params);
    setGaps(data);
  }, [filter]);

  useEffect(() => {
    fetchGaps();
  }, [fetchGaps]);

  const handleMarkReviewed = async (id) => {
    await updateGap(id, { reviewed: true, reviewed_by: "volunteer" });
    fetchGaps();
  };

  const handleExportCsv = () => {
    const headers = ["Question", "Bot Response", "Answered", "Feedback", "Flagged", "Reviewed", "Timestamp"];
    const rows = gaps.map((g) => [
      `"${(g.question || "").replace(/"/g, '""')}"`,
      `"${(g.bot_response || "").replace(/"/g, '""')}"`,
      g.was_answered ? "Yes" : "No",
      g.user_feedback || "",
      g.flagged ? "Yes" : "No",
      g.reviewed ? "Yes" : "No",
      g.timestamp || "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "knowledge-gaps.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 4 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Knowledge Gaps
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, val) => val && setFilter(val)}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="unanswered">Unanswered</ToggleButton>
          <ToggleButton value="flagged">Flagged</ToggleButton>
          <ToggleButton value="unreviewed">Unreviewed</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="outlined" size="small" onClick={handleExportCsv}>
          Export CSV
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: "divider" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Response</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Feedback</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {gaps.map((gap) => (
              <TableRow key={gap.id}>
                <TableCell sx={{ maxWidth: 240, fontSize: 13 }}>{gap.question}</TableCell>
                <TableCell sx={{ maxWidth: 320, fontSize: 13 }}>
                  {(gap.bot_response || "").slice(0, 150)}
                  {(gap.bot_response || "").length > 150 ? "..." : ""}
                </TableCell>
                <TableCell>
                  {!gap.was_answered && (
                    <Chip label="Unanswered" size="small" color="warning" variant="outlined" />
                  )}
                  {gap.flagged && (
                    <Chip label="Flagged" size="small" color="error" variant="outlined" sx={{ ml: 0.5 }} />
                  )}
                  {gap.reviewed && (
                    <Chip label="Reviewed" size="small" color="success" variant="outlined" sx={{ ml: 0.5 }} />
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {gap.user_feedback || "—"}
                  {gap.volunteer_notes && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {gap.volunteer_notes}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {!gap.reviewed && (
                    <Button
                      size="small"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={() => handleMarkReviewed(gap.id)}
                    >
                      Mark Reviewed
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {gaps.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.disabled" }}>
                  No knowledge gaps found for this filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
